import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

const DEFAULT_EXPAND_COUNT = 5;

/**
 * @typedef {function} RenderToggle
 * @returns {React.ReactElement}
 * @param {string} text - Text for the button
 * @param {function} onToggle - Function to toggle the list
 * @param {boolean} isExpanded - Whether the list is expanded
 */

/**
 * A reusable component that displays a limited number of children
 * with a "Show more" button to reveal additional children.
 *
 * @param {Object} props
 * @param {React.ReactNode[]} props.children - Array of child elements to display
 * @param {number} props.initialCount - Number of children to show initially (default: 4)
 * @param {string} props.showMoreText - Text for the "Show more" button (default: "Show more")
 * @param {string} props.showLessText - Text for the "Show less" button (default: "Show less")
 * @param {RenderToggle} props.ShowToggle - Optional custom render function for the button
 */
export const ShowMoreList = ({
  children,
  initialCount = 4,
  showMoreText,
  showLessText,
  renderToggle,
}) => {
  const [count, setCount] = useState(initialCount);
  // Ensure children is an array
  const childrenArray = useMemo(
    () => (Array.isArray(children) ? children : [children]),
    [children],
  );
  const isFullyExpanded = useMemo(
    () => count >= childrenArray.length,
    [count, childrenArray],
  );

  const visibleChildren = useMemo(() => {
    return isFullyExpanded ? childrenArray : childrenArray.slice(0, count);
  }, [childrenArray, count, isFullyExpanded]);

  const hasMore = useMemo(
    () => childrenArray.length > initialCount,
    [childrenArray, initialCount],
  );

  const handleToggle = useCallback(() => {
    const newCount = isFullyExpanded
      ? initialCount
      : count + DEFAULT_EXPAND_COUNT;
    setCount(newCount);
  }, [isFullyExpanded, count, initialCount]);

  return (
    <>
      {visibleChildren.map((child, index) => {
        // Use child's key if it's a React element with a key, otherwise use index
        const key =
          React.isValidElement(child) && child.key !== null
            ? child.key
            : `show-more-item-${index}`;
        return <React.Fragment key={key}>{child}</React.Fragment>;
      })}
      {hasMore &&
        renderToggle({
          text: isFullyExpanded ? showLessText : showMoreText,
          onToggle: handleToggle,
          isFullyExpanded,
        })}
    </>
  );
};

ShowMoreList.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  initialCount: PropTypes.number,
  showMoreText: PropTypes.string,
  showLessText: PropTypes.string,
  renderToggle: PropTypes.func.isRequired,
};
